import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function AdminContent() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-content"],
    queryFn: async () => (await api.get("/admin/content")).data,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const updateListItem = (listKey, index, field, value) => {
    setForm((f) => {
      const updatedList = [...f[listKey]];
      updatedList[index] = { ...updatedList[index], [field]: value };
      return { ...f, [listKey]: updatedList };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", {
        hero_title: form.hero_title,
        hero_subtitle: form.hero_subtitle,
        whatsapp_number: form.whatsapp_number,
        cta_title: form.cta_title,
        cta_subtitle: form.cta_subtitle,
        services: form.services,
        sectors: form.sectors,
      });
      toast({ title: "Kaydedildi", description: "Site içeriği güncellendi." });
    } catch (err) {
      toast({ variant: "destructive", title: "Hata", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-content-page" className="space-y-6">
      <div>
        <h1 className="text-xl font-medium text-white">İçerik Yönetimi</h1>
        <p className="mt-1 text-sm text-slate-400">Ana sayfa metinlerini buradan güncelleyebilirsiniz.</p>
      </div>

      <Card className="border-white/10 bg-[#0A0A0A]">
        <CardHeader><CardTitle className="text-base text-white">Hero Bölümü</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-400">Başlık</Label>
            <Input value={form.hero_title} onChange={(e) => updateField("hero_title", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="content-hero-title-input" />
          </div>
          <div>
            <Label className="text-slate-400">Alt Başlık</Label>
            <Textarea value={form.hero_subtitle} onChange={(e) => updateField("hero_subtitle", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="content-hero-subtitle-input" />
          </div>
          <div>
            <Label className="text-slate-400">WhatsApp Numarası (ülke kodu ile, örn: 905343323111)</Label>
            <Input value={form.whatsapp_number} onChange={(e) => updateField("whatsapp_number", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="content-whatsapp-number-input" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-[#0A0A0A]">
        <CardHeader><CardTitle className="text-base text-white">Son CTA Bölümü</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-400">Başlık</Label>
            <Input value={form.cta_title} onChange={(e) => updateField("cta_title", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="content-cta-title-input" />
          </div>
          <div>
            <Label className="text-slate-400">Alt Başlık</Label>
            <Textarea value={form.cta_subtitle} onChange={(e) => updateField("cta_subtitle", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="content-cta-subtitle-input" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-[#0A0A0A]">
        <CardHeader><CardTitle className="text-base text-white">Hizmetler</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {form.services.map((service, i) => (
            <div key={i} className="space-y-2 border-b border-white/5 pb-4 last:border-0 last:pb-0">
              <Input value={service.title} onChange={(e) => updateListItem("services", i, "title", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid={`content-service-title-${i}`} />
              <Textarea value={service.description} onChange={(e) => updateListItem("services", i, "description", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid={`content-service-description-${i}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-[#0A0A0A]">
        <CardHeader><CardTitle className="text-base text-white">Referanslar / Sektörler</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {form.sectors.map((sector, i) => (
            <div key={i} className="space-y-2 border-b border-white/5 pb-4 last:border-0 last:pb-0">
              <Input value={sector.title} onChange={(e) => updateListItem("sectors", i, "title", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid={`content-sector-title-${i}`} />
              <Textarea value={sector.description} onChange={(e) => updateListItem("sectors", i, "description", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid={`content-sector-description-${i}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="rounded-full bg-gold px-8 text-black hover:bg-gold-light" data-testid="content-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tüm Değişiklikleri Kaydet"}
      </Button>
    </div>
  );
}
